import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';
import { Github, Linkedin, Mail, ExternalLink, Code, Database, Shield, Download, Phone, Gamepad2 } from 'lucide-react';

function Portfolio() {
    const navigate = useNavigate();

    const handleEmailClick = (e) => {
        e.preventDefault();
        const email = 'anjoejames007@gmail.com';
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

        if (isMobile) {
            window.location.href = `mailto:${email}`;
        } else {
            window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${email}`, '_blank');
        }
    };

    // Scroll to top or specific section when component mounts
    useEffect(() => {
        // Check if there's a hash in the URL
        if (window.location.hash === '#projects') {
            // Small delay to ensure DOM is ready
            setTimeout(() => {
                const projectsSection = document.getElementById('projects');
                if (projectsSection) {
                    projectsSection.scrollIntoView({ behavior: 'smooth' });
                }
                // Clear the hash so refreshing goes to top
                window.history.replaceState(null, null, window.location.pathname);
            }, 100);
        } else {
            window.scrollTo(0, 0);
        }
    }, []);

    const skills = [
        { category: "Frontend", items: "React.js, React Native, JavaScript (ES6+)" },
        { category: "Backend", items: "Node.js, Express.js, Java, Python" },
        { category: "Database", items: "MongoDB, MySQL" },
        { category: "Web3/Security", items: "Blockchain, IPFS, AES-256" },
        { category: "Tools", items: "Git, GitHub, Postman, VS Code"}
    ];

    const projects = [
        {
            title: "S.O.U.L (Secure Online Unified Locker)",
            tag: "Flagship Project",
            desc: "A decentralized digital legacy planning platform. Enables users to securely store digital assets and automate their transfer to beneficiaries upon death using Blockchain and IPFS.",
            tech: ["React Native", "Node.js", "Blockchain", "IPFS"],
            link: "/soul-project",
            engineering: {
                problem: "People lose access to digital assets when loved ones pass away. No secure, decentralized way to transfer digital legacy.",
                role: "Full-stack developer. Built entire system from architecture design to deployment—backend APIs, blockchain integration, mobile app, and IPFS storage.",
                decisions: "Chose blockchain for immutable ownership records and IPFS for decentralized file storage. Used React Native for cross-platform mobile development to reduce development time.",
                security: "Implemented end-to-end encryption for stored files, blockchain-based access control, and secure beneficiary verification system."
            }
        },
        {
            title: "LAN-Based File Sharing System",
            tag: "Mini Project",
            desc: "A comprehensive LAN-based file-sharing system with public and private room-based sharing. Features real-time WebSocket updates, AES-256 encrypted authentication, SSL/TLS security, and support for files up to 100MB with upload progress tracking.",
            tech: ["Node.js", "Express.js", "MySQL", "Socket.IO", "AES-256"],
            link: "/lan-project",
            engineering: {
                problem: "Teams needed a secure way to share files within local networks without relying on external cloud services.",
                role: "Full-stack developer. Built file upload system, real-time sync with WebSockets, room management, and authentication.",
                decisions: "Used Socket.IO for real-time updates, implemented room-based access control, and added SSL/TLS for encrypted file transfers.",
                security: "AES-256 encrypted user authentication, file size limits to prevent abuse, session-based access control, and HTTPS for all communications."
            }
        },
        {
            title: "Secure OTP Password Manager",
            tag: "Freelance",
            desc: "A modern OTP-based authentication system with integrated password manager featuring AES-256 encryption, email-based one-time passwords, ultra-modern dark theme UI with neon accents, and advanced glassmorphism effects.",
            tech: ["Node.js", "Express.js", "MySQL", "AES-256", "Nodemailer"],
            link: "/otp-project",
            engineering: {
                problem: "Traditional password managers require remembering a master password. Client needed a more secure, OTP-based authentication system.",
                role: "Backend and frontend developer. Built authentication system, password encryption logic, email OTP service, and modern UI.",
                decisions: "Used AES-256 for password encryption, email-based OTP instead of SMS for cost-effectiveness, and MySQL for relational data integrity.",
                security: "Implemented session management, encrypted password storage, rate limiting on OTP requests, and secure password generation algorithms."
            }
        },
        {
            title: "Health Care IoT Dashboard",
            tag: "Freelance",
            desc: "A comprehensive real-time health monitoring system for patients, doctors, and bystanders. The platform ingests live vital data from ESP32 IoT devices and distributes it securely across a modern Web Dashboard and React Native mobile application.",
            tech: ["React.js", "React Native", "Node.js", "MongoDB", "MQTT", "Socket.io"],
            link: "/health-iot-project",
            engineering: {
                problem: "Patients and doctors needed a real-time health monitoring system that could track vital signs remotely and alert stakeholders during emergencies.",
                role: "Full-stack developer. Built the complete system from IoT data ingestion to web and mobile dashboards—backend APIs, MQTT integration, real-time WebSockets, and cross-platform apps.",
                decisions: "Used HiveMQ Cloud as MQTT broker for IoT data ingestion, MongoDB for time-series health data, Socket.io for real-time updates, React for web dashboard, and React Native (Expo) for mobile apps with QR code scanning.",
                security: "JWT-based session security, Bcrypt password hashing, role-based access control (Doctor/Patient/Bystander), and encrypted MQTT communication."
            }
        },
        {
            title: "SPAN Website",
            tag: "Freelance",
            desc: "A full-stack website for SPAN (Suicide Prevention Awareness Network - Research & Training), a non-profit dedicated to mental health awareness. Features a static frontend with admin panel, blog system with comments, gallery, newsletter subscriptions, testimonials, and Cloudinary image optimization.",
            tech: ["Node.js", "Express.js", "SQLite", "Bootstrap 5", "Cloudinary", "JWT"],
            link: "/span-project",
            engineering: {
                problem: "A non-profit needed a cost-effective, maintainable web presence with blog management, image gallery, and admin capabilities without expensive hosting or database services.",
                role: "Full-stack developer. Built the entire system from database design to deployment—Express backend, SQLite integration, Cloudinary image handling, JWT authentication, and admin panel.",
                decisions: "Chose SQLite (via Turso/libSQL) for zero-config, cost-free database hosting. Used Cloudinary for CDN-backed image optimization to reduce bandwidth costs. Bootstrap 5 for rapid, responsive UI without custom CSS frameworks.",
                security: "JWT-based admin authentication, bcrypt password hashing, Multer for secure file uploads, and environment variable configuration for secrets."
            }
        }
    ];

    return (
        <div className="portfolio">
            {/* Hero Section */}
            <header className="hero">
                <div className="hero-content">
                    <div className="profile-img-container" onClick={() => navigate('/certificates')} style={{ cursor: 'pointer' }}>
                        {/* Make sure your image is named profile.jpg in the public folder */}
                        <img src="/profile.jpg" alt="Anjoe James - Full Stack Developer" className="profile-img" loading="eager" />
                    </div>
                    <h1>Anjoe James</h1>
                    <h2>Full Stack Developer</h2>
                    <p className="positioning-line">
                        Focused on secure systems and real-world applications
                    </p>
                    <p>
                        Experienced in building production-style systems using Node.js, React, databases,
                        and modern AI-assisted workflows. Computer Science graduate from KTU with
                        a passion for building decentralized applications and secure backend architectures.
                    </p>

                    <div className="social-links">
                        <a href="mailto:anjoejames007@gmail.com" onClick={(e) => handleEmailClick(e)} target="_blank" rel="noreferrer" className="btn primary">
                            <Mail size={18} /> Contact Me
                        </a>
                        <a href="/Anjoe_James_Resume.pdf" download className="btn primary">
                            <Download size={18} /> Download Resume
                        </a>
                        <a href="https://github.com/ANJOEJAMES" target="_blank" rel="noreferrer" className="btn secondary">
                            <Github size={18} /> GitHub
                        </a>
                        <a href="https://www.linkedin.com/in/anjoe-james" target="_blank" rel="noreferrer" className="btn secondary">
                            <Linkedin size={18} /> LinkedIn
                        </a>
                    </div>
                </div>
            </header>

            {/* Skills Section */}
            <section className="section skills-section">
                <h3>Technical Arsenal</h3>
                <div className="skills-grid">
                    {skills.map((skill, index) => (
                        <div key={index} className="skill-card">
                            {index === 0 && <Code className="icon" />}
                            {index === 1 && <Database className="icon" />}
                            {index > 1 && <Shield className="icon" />}
                            <h4>{skill.category}</h4>
                            <p>{skill.items}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Why Hire Me Section */}
            <section className="section">
                <h3>Why Hire Me</h3>
                <div className="skills-grid">
                    <div className="skill-card">
                        <Code className="icon" />
                        <h4>Systems Thinking</h4>
                        <p>I think in systems, not just UI. I focus on data flow, API design, and how components interact across the stack.</p>
                    </div>
                    <div className="skill-card">
                        <Shield className="icon" />
                        <h4>Security & Backend Focus</h4>
                        <p>I prioritize security, data handling, and backend logic. From AES-256 encryption to session management, I build with security in mind.</p>
                    </div>
                    <div className="skill-card">
                        <Database className="icon" />
                        <h4>AI-Assisted Development</h4>
                        <p>I can quickly ship features using AI tools while understanding every line of code. AI accelerates my work, but I validate and own all decisions.</p>
                    </div>
                    <div className="skill-card">
                        <Code className="icon" />
                        <h4>Fast Learner</h4>
                        <p>I adapt quickly and improve with feedback. I'm eager to learn from experienced developers and contribute to team growth.</p>
                    </div>
                </div>
            </section>


            {/* How I Build Software Section */}
            <section className="section">
                <h3>How I Build Software</h3>
                <div className="workflow">
                    <div className="workflow-step">
                        <div className="step-number">1</div>
                        <h4>Understanding the Problem</h4>
                        <p>I start by clarifying requirements, identifying edge cases, and understanding the user's actual needs beyond the initial request.</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">2</div>
                        <h4>Designing Data Flow & Architecture</h4>
                        <p>I map out database schemas, API endpoints, and component relationships before writing code. Planning prevents costly rewrites.</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">3</div>
                        <h4>Implementing Secure APIs & Logic</h4>
                        <p>I build with security first—input validation, authentication, encryption, and proper error handling are non-negotiable.</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">4</div>
                        <h4>Using AI Tools Responsibly</h4>
                        <p>I leverage AI to accelerate boilerplate code and explore solutions, but I review, refactor, and understand every line before deployment.</p>
                    </div>
                    <div className="workflow-step">
                        <div className="step-number">5</div>
                        <h4>Testing, Validating & Refining</h4>
                        <p>I test edge cases, validate data flows, and refine based on feedback. Software is never "done"—it evolves with use.</p>
                    </div>
                </div>
            </section>

            {/* Projects Section */}
            <section className="section" id="projects">
                <h3>Featured Projects</h3>
                <div className="projects-grid">
                    {projects.map((project, index) => (
                        <div
                            key={index}
                            className={`project-card ${project.link ? 'clickable' : ''}`}
                            onClick={() => project.link && navigate(project.link)}
                            style={project.link ? { cursor: 'pointer' } : {}}
                        >
                            <div className="card-header">
                                <h4>{project.title}</h4>
                                <span className="tag">{project.tag}</span>
                            </div>
                            <p>{project.desc}</p>
                            <div className="tech-stack">
                                {project.tech.map((t, i) => (
                                    <span key={i} className="tech-pill">{t}</span>
                                ))}
                            </div>
                            {project.engineering && (
                                <div className="engineering-breakdown">
                                    <h5>Engineering Breakdown</h5>
                                    <div className="breakdown-item">
                                        <strong>Problem Solved:</strong>
                                        <p>{project.engineering.problem}</p>
                                    </div>
                                    <div className="breakdown-item">
                                        <strong>My Role:</strong>
                                        <p>{project.engineering.role}</p>
                                    </div>
                                    <div className="breakdown-item">
                                        <strong>Key Technical Decisions:</strong>
                                        <p>{project.engineering.decisions}</p>
                                    </div>
                                    <div className="breakdown-item">
                                        <strong>Security/Performance:</strong>
                                        <p>{project.engineering.security}</p>
                                    </div>
                                </div>
                            )}
                            {project.link && (
                                <div className="view-details">
                                    <span className="view-details-text">Click to view details →</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* AI-Assisted Development Section */}
            <section className="section">
                <h3>AI-Assisted Development (Transparency)</h3>
                <div className="blog-card">
                    <p>
                        I use AI tools like <strong>Antigravity</strong> to accelerate development and explore solutions faster.
                        However, I treat AI as a productivity tool, not a replacement for engineering thinking.
                    </p>
                    <ul style={{ marginTop: '1rem', lineHeight: '1.8' }}>
                        <li>✓ I validate and refactor all AI-generated code</li>
                        <li>✓ I understand every line before deployment</li>
                        <li>✓ I use AI to speed up boilerplate, not to avoid learning</li>
                        <li>✓ I take full ownership of all technical decisions</li>
                    </ul>
                    <p style={{ marginTop: '1rem', fontStyle: 'italic' }}>
                        AI helps me ship faster, but my engineering judgment ensures quality and security.
                    </p>
                </div>
            </section>

            {/* Education & Blog */}
            <section className="section split-section">
                <div className="column">
                    <h3>Education</h3>
                    <div className="timeline-item">
                        <h4>B.Tech in Computer Science</h4>
                        <p>College of Engineering Kallooppara (KTU)</p>
                        <span className="date">2022 - 2026</span>
                    </div>
                </div>
                <div className="column">
                    <h3>Personal Blog</h3>
                    <div className="blog-card">
                        <h4>In Conversation with Myself</h4>
                        <p>A collection of poetry and personal reflections.</p>
                        <a href="https://anjoejames.blogspot.com" target="_blank" rel="noreferrer" className="link-text">
                            Visit Blog <ExternalLink size={14} />
                        </a>
                    </div>
                </div>
            </section>

            {/* What I Am Looking For Section */}
            <section className="section">
                <h3>What I Am Looking For</h3>
                <div className="blog-card">
                    <p>
                        I'm seeking <strong>Junior / Entry-Level Full-Stack or Backend Developer roles</strong> where
                        I can contribute to real-world projects while learning from experienced engineers.
                    </p>
                    <p style={{ marginTop: '1rem' }}>
                        I'm particularly interested in backend-heavy or full-stack teams working on secure systems,
                        APIs, databases, and scalable architectures. I thrive in fast-paced environments where I can
                        ship features, receive feedback, and continuously improve.
                    </p>
                    <p style={{ marginTop: '1rem' }}>
                        <strong>What I bring:</strong> Strong fundamentals, security-first mindset, ability to learn quickly,
                        and genuine enthusiasm for building software that solves real problems.
                    </p>
                </div>
            </section>

             {/* Contact Information Section */}
             <section className="section contact-section">
                 <h3>Let's Connect</h3>
                 <div className="blog-card" style={{ marginBottom: '2rem', textAlign: 'center' }}>
                     <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                         <strong>Currently seeking opportunities as a Junior Full-Stack or Backend Developer.</strong>
                     </p>
                     <p>
                         If my projects align with your team's needs, I would love to connect and discuss how I can contribute.
                     </p>
                 </div>
                 <div className="contact-info-grid">
                     <div className="contact-card">
                         <div className="contact-icon">
                             <Mail size={32} />
                         </div>
                         <h4>Email</h4>
                         <a href="mailto:anjoejames007@gmail.com" onClick={(e) => handleEmailClick(e)} target="_blank" rel="noreferrer" className="contact-link">
                             anjoejames007@gmail.com
                         </a>
                     </div>
                     <div className="contact-card">
                         <div className="contact-icon">
                             <Phone size={32} />
                         </div>
                         <h4>Phone</h4>
                         <a href="tel:+919497879663" className="contact-link">
                             +91 9497879663
                         </a>
                     </div>
                 </div>
             </section>

              {/* Games Teaser Section */}
             <section className="section games-teaser-section">
                 <div className="games-teaser-card" onClick={() => navigate('/games')}>
                     <div className="games-teaser-glow" />
                     <Gamepad2 className="games-teaser-icon" />
                     <h3>Psst... Need a Break?</h3>
                     <p>
                         You scrolled all the way down — I respect that. Here's a reward:
                         I build mini games too. Come play.
                     </p>
                     <span className="games-teaser-cta">Browse Games →</span>
                 </div>
             </section>

              <footer>
                 <p>© {new Date().getFullYear()} Anjoe James. Built with React.</p>
             </footer>
        </div>
    );
}

export default Portfolio;
